/* eslint-disable jsx-a11y/label-has-associated-control */
/**
 * CaseSetupMessaging
 *
 * Full-height SMS and email preview panels for case setup intro messages.
 * Each fills the entire right column. State is controlled by parent so
 * the completion handler can access the edited message content for sending.
 *
 * When `unavailableReason` is set the panel shows a centered explanation
 * instead of loading the draft — no API call is fired.
 *
 * Both tabs show a read-only communication history above the draft compose
 * area so users have context before sending the intro message.
 */
import { format, isToday } from 'date-fns';
import { AlertTriangle, ArrowLeft, ArrowRight, Info, Loader2, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { EmailBodyEditor } from '@/components/ui/email-body-editor';
import { cn } from '@/lib/utils';
import { useGetClientCommunicationsQuery } from '@/services/api/clientCommunicationsService';
import {
  useCaseSetupDraftIntroEmailMutation,
  useCaseSetupDraftIntroSmsMutation,
} from '@/services/api/generated/caseSetupService';
import type { UnifiedCommunication } from '@/services/types/client-communications-types';
import { MatterType } from '@/services/types/client-intake-types';

interface SmsPreviewProps {
  matterId: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  message: string;
  onMessageChange: (message: string) => void;
  unavailableReason?: string;
  onLoadingChange?: (loading: boolean) => void;
  languageOverride?: string;
}

interface EmailPreviewProps {
  matterId: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  subject: string;
  onSubjectChange: (subject: string) => void;
  body: string;
  onBodyChange: (body: string) => void;
  unavailableReason?: string;
  onLoadingChange?: (loading: boolean) => void;
  languageOverride?: string;
}

// ── Case type helpers ────────────────────────────────────────────────────────

const KNOWN_CASE_TYPES = new Set<string>([
  MatterType.MOTOR_VEHICLE_ACCIDENT,
  MatterType.PREMISES_LIABILITY,
]);

const isKnownCaseType = (matterType: string | undefined): boolean =>
  !!matterType && KNOWN_CASE_TYPES.has(matterType);

// ── Shared unavailable banner ────────────────────────────────────────────────

const UnavailableBanner = ({ reason }: { reason: string }) => (
  <div className="flex h-full items-center justify-center p-6">
    <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40 p-4 max-w-xs text-sm text-amber-800 dark:text-amber-300">
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
      <span>{reason}</span>
    </div>
  </div>
);

const ManualReviewBanner = ({ matterType }: { matterType: string }) => (
  <div className="shrink-0 flex items-start gap-2 border-b border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 px-3 py-2">
    <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" />
    <p className="text-[11px] leading-snug font-medium text-blue-700 dark:text-blue-300">
      This is a <span className="font-semibold">{matterType}</span> case.
      The message below was generated with a default template and may need
      adjustments. Please review and edit before sending.
    </p>
  </div>
);

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatMessageTime = (timestamp: string) => {
  const date = new Date(timestamp);
  if (isToday(date)) return format(date, 'h:mm a');
  return format(date, 'MMM d, yyyy h:mm a');
};

// ── SMS History ─────────────────────────────────────────────────────────────

const SmsHistory = ({ matterId }: { matterId: string }) => {
  const { data, isLoading } = useGetClientCommunicationsQuery({
    matterId,
    params: { type: 'sms', limit: 50 },
  });
  const scrollRef = useRef<HTMLDivElement>(null);

  const messages = data?.communications ?? [];

  useEffect(() => {
    if (scrollRef.current && messages.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <p className="px-3 py-4 text-center text-xs text-zinc-400 dark:text-zinc-500">
        No previous text messages
      </p>
    );
  }

  // API returns newest-first; reverse so oldest is at top
  const sorted = [...messages].reverse();

  return (
    <div ref={scrollRef} className="flex flex-col gap-1.5 overflow-y-auto px-3 py-2">
      {sorted.map((msg, i) => {
        const isOutbound = msg.direction === 'outbound';
        // Show timestamp if last message or next message has a different direction
        const showTime =
          i === sorted.length - 1 || sorted[i + 1].direction !== msg.direction;

        return (
          <div key={msg.id}>
            <div
              className={cn(
                'flex',
                isOutbound ? 'justify-end' : 'justify-start',
              )}
            >
              <div
                className={cn(
                  'max-w-[80%] rounded-2xl px-3 py-1.5 text-[12px] leading-[1.45]',
                  isOutbound
                    ? 'bg-[#3478f6] dark:bg-[#0a84ff] text-white rounded-br-sm'
                    : 'bg-zinc-300 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-bl-sm',
                )}
              >
                {msg.content}
              </div>
            </div>
            {showTime && (
              <p
                className={cn(
                  'mt-0.5 text-[10px] text-zinc-400 dark:text-zinc-500 tabular-nums',
                  isOutbound ? 'text-right mr-1' : 'text-left ml-1',
                )}
              >
                {formatMessageTime(msg.timestamp)}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Email History ───────────────────────────────────────────────────────────

const EmailHistory = ({ matterId }: { matterId: string }) => {
  const { data, isLoading } = useGetClientCommunicationsQuery({
    matterId,
    params: { type: 'email', limit: 20 },
  });

  const emails = data?.communications ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <p className="px-4 py-4 text-center text-xs text-zinc-400 dark:text-zinc-500">
        No previous emails
      </p>
    );
  }

  // API returns newest-first; reverse so oldest is at top
  const sorted = [...emails].reverse();

  return (
    <div className="overflow-y-auto divide-y divide-zinc-100 dark:divide-zinc-800">
      {sorted.map((email) => (
        <EmailHistoryRow key={email.id} email={email} />
      ))}
    </div>
  );
};

const EmailHistoryRow = ({ email }: { email: UnifiedCommunication }) => {
  const isOutbound = email.direction === 'outbound';
  const date = new Date(email.timestamp);
  const dateStr = isToday(date)
    ? format(date, 'h:mm a')
    : format(date, 'MMM d, yyyy');

  return (
    <div className="flex items-center gap-2 px-4 py-2 text-xs">
      {isOutbound ? (
        <ArrowRight className="h-3 w-3 shrink-0 text-blue-500" />
      ) : (
        <ArrowLeft className="h-3 w-3 shrink-0 text-zinc-400" />
      )}
      <span
        className={cn(
          'truncate font-medium min-w-0',
          isOutbound
            ? 'text-zinc-700 dark:text-zinc-300'
            : 'text-zinc-600 dark:text-zinc-400',
        )}
      >
        {email.subject || '(no subject)'}
      </span>
      <span className="ml-auto shrink-0 text-zinc-400 dark:text-zinc-500 tabular-nums whitespace-nowrap">
        {dateStr}
      </span>
    </div>
  );
};

// ── SMS Preview ──────────────────────────────────────────────────────────────

export const CaseSetupSmsPreview = ({
  matterId,
  enabled,
  onEnabledChange,
  message,
  onMessageChange,
  unavailableReason,
  onLoadingChange,
  languageOverride,
}: SmsPreviewProps) => {
  const [draftIntroSms, { isLoading }] = useCaseSetupDraftIntroSmsMutation();
  const hasFetchedRef = useRef<string | null>(null);
  const isInitialFetchRef = useRef(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  // Recalculate textarea height when message changes or loading finishes.
  // The tab uses forceMount + data-[state=inactive]:hidden (display:none), so scrollHeight
  // is 0 when the textarea first renders. We use an IntersectionObserver to catch the
  // moment the element becomes visible and recalculate then.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el || isLoading) return undefined;

    const recalc = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };

    // If already visible, recalculate immediately
    if (el.scrollHeight > 0) {
      recalc();
      return undefined;
    }

    // Otherwise wait until the tab becomes visible
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        recalc();
        observer.disconnect();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [message, isLoading]);

  const [draftMatterType, setDraftMatterType] = useState<string | undefined>();

  const fetchDraft = useCallback(() => {
    draftIntroSms({
      matterId,
      draftCaseSetupMessageRequest: {
        matter_id: matterId,
        language_override: languageOverride ?? null,
      },
    })
      .unwrap()
      .then((result) => {
        onMessageChange(result.message);
        // Only auto-enable on initial load; language changes preserve the user's choice
        if (isInitialFetchRef.current) {
          onEnabledChange(true);
          isInitialFetchRef.current = false;
        }
        setDraftMatterType(result.matter_type);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to draft intro SMS:', err);
      });
  }, [matterId, draftIntroSms, onMessageChange, onEnabledChange, languageOverride]);

  const draftKey = `${matterId}:${languageOverride ?? ''}`;

  useEffect(() => {
    if (unavailableReason) return;
    if (hasFetchedRef.current === draftKey) return;
    hasFetchedRef.current = draftKey;
    fetchDraft();
  }, [draftKey, unavailableReason, fetchDraft]);

  const handleReset = useCallback(() => {
    hasFetchedRef.current = null;
    isInitialFetchRef.current = true; // Reset re-enables on explicit reset
    fetchDraft();
    hasFetchedRef.current = draftKey;
  }, [draftKey, fetchDraft]);

  if (unavailableReason) {
    return <UnavailableBanner reason={unavailableReason} />;
  }

  return (
    <div className="flex h-full flex-col">
      {/* Banner — always interactive */}
      <div className="shrink-0 flex items-center gap-2 border-b border-zinc-200 dark:border-zinc-800 px-3 py-2 bg-zinc-50 dark:bg-zinc-900">
        <Checkbox
          id="case-setup-sms-enabled"
          checked={enabled}
          onCheckedChange={(v) => onEnabledChange(v === true)}
          className="size-4 rounded-[4px] border-indigo-300 dark:border-indigo-600 data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500 data-[state=checked]:text-white"
        />
        <label htmlFor="case-setup-sms-enabled" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
          Send Welcome SMS
        </label>
        <button
          type="button"
          onClick={handleReset}
          className="ml-auto flex items-center gap-1 text-[10px] text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
          title="Reset to AI-generated draft"
        >
          <RotateCcw className="h-3 w-3" />
          Reset
        </button>
      </div>
      {/* Content — muted when disabled */}
      <div
        className={cn(
          'flex-1 flex flex-col transition-opacity duration-150',
          !enabled && 'opacity-40 pointer-events-none select-none',
        )}
      >
        <div className="flex flex-1 flex-col overflow-hidden bg-[#e5e5ea] dark:bg-zinc-950">
          {/* History section */}
          <div className="shrink-0 max-h-[40%] flex flex-col overflow-hidden">
            <div className="shrink-0 px-3 pt-2 pb-1">
              <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                Previous messages
              </span>
            </div>
            <div className="flex-1 overflow-y-auto">
              <SmsHistory matterId={matterId} />
            </div>
          </div>

          {/* Divider */}
          <div className="shrink-0 border-t border-zinc-300/60 dark:border-zinc-700/60" />

          {!isLoading && !isKnownCaseType(draftMatterType) && draftMatterType && (
            <ManualReviewBanner matterType={draftMatterType} />
          )}

          {/* Draft compose area */}
          <div className="flex-1 flex flex-col justify-start overflow-y-auto p-3 pb-2">
            <div className="flex flex-col items-end">
              {isLoading ? (
                <div className="w-[92%] rounded-[18px] rounded-br-[4px] bg-[#3478f6]/60 dark:bg-[#0a84ff]/60 shadow-sm px-3.5 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-white/70" />
                    <span className="text-[12px] text-white/70">Generating draft...</span>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    <div className="h-2.5 w-full rounded bg-white/15 animate-pulse" />
                    <div className="h-2.5 w-4/5 rounded bg-white/15 animate-pulse" />
                    <div className="h-2.5 w-3/5 rounded bg-white/15 animate-pulse" />
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-[92%] rounded-[18px] rounded-br-[4px] bg-[#3478f6] dark:bg-[#0a84ff] shadow-sm">
                    <textarea
                      ref={textareaRef}
                      value={message}
                      onChange={(e) => {
                        onMessageChange(e.target.value);
                        const el = e.target;
                        el.style.height = 'auto';
                        el.style.height = `${el.scrollHeight}px`;
                      }}
                      placeholder="Type message..."
                      className="w-full resize-none border-0 bg-transparent px-3.5 py-2.5 text-[13px] leading-[1.5] text-white placeholder:text-white/40 outline-none"
                    />
                  </div>
                  <span className="text-[10px] text-zinc-400 dark:text-zinc-500 tabular-nums mt-1 mr-1">
                    {message.length} chars
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Email Preview ────────────────────────────────────────────────────────────

export const CaseSetupEmailPreview = ({
  matterId,
  enabled,
  onEnabledChange,
  subject,
  onSubjectChange,
  body,
  onBodyChange,
  unavailableReason,
  onLoadingChange,
  languageOverride,
}: EmailPreviewProps) => {
  const [draftIntroEmail, { isLoading }] =
    useCaseSetupDraftIntroEmailMutation();
  const hasFetchedRef = useRef<string | null>(null);
  const isInitialFetchRef = useRef(true);
  const [draftMatterType, setDraftMatterType] = useState<string | undefined>();

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const fetchDraft = useCallback(() => {
    draftIntroEmail({
      matterId,
      draftCaseSetupMessageRequest: {
        matter_id: matterId,
        language_override: languageOverride ?? null,
      },
    })
      .unwrap()
      .then((result) => {
        onSubjectChange(result.subject);
        onBodyChange(result.body_html);
        // Only auto-enable on initial load; language changes preserve the user's choice
        if (isInitialFetchRef.current) {
          onEnabledChange(true);
          isInitialFetchRef.current = false;
        }
        setDraftMatterType(result.matter_type);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to draft intro email:', err);
      });
  }, [matterId, draftIntroEmail, onSubjectChange, onBodyChange, onEnabledChange, languageOverride]);

  const draftKey = `${matterId}:${languageOverride ?? ''}`;

  useEffect(() => {
    if (unavailableReason) return;
    if (hasFetchedRef.current === draftKey) return;
    hasFetchedRef.current = draftKey;
    fetchDraft();
  }, [draftKey, unavailableReason, fetchDraft]);

  const handleReset = useCallback(() => {
    hasFetchedRef.current = null;
    isInitialFetchRef.current = true; // Reset re-enables on explicit reset
    fetchDraft();
    hasFetchedRef.current = draftKey;
  }, [draftKey, fetchDraft]);

  if (unavailableReason) {
    return <UnavailableBanner reason={unavailableReason} />;
  }

  return (
    <div className="flex h-full flex-col">
      {/* Banner — always interactive */}
      <div className="shrink-0 flex items-center gap-2 border-b border-zinc-200 dark:border-zinc-800 px-3 py-2 bg-zinc-50 dark:bg-zinc-900">
        <Checkbox
          id="case-setup-email-enabled"
          checked={enabled}
          onCheckedChange={(v) => onEnabledChange(v === true)}
          className="size-4 rounded-[4px] border-indigo-300 dark:border-indigo-600 data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500 data-[state=checked]:text-white"
        />
        <label htmlFor="case-setup-email-enabled" className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
          Send Welcome Email
        </label>
        <button
          type="button"
          onClick={handleReset}
          className="ml-auto flex items-center gap-1 text-[10px] text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
          title="Reset to AI-generated draft"
        >
          <RotateCcw className="h-3 w-3" />
          Reset
        </button>
      </div>
      {/* Content — muted when disabled */}
      <div
        className={cn(
          'flex-1 flex flex-col transition-opacity duration-150',
          !enabled && 'opacity-40 pointer-events-none select-none',
        )}
      >
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* History section */}
          <div className="shrink-0 max-h-[40%] flex flex-col overflow-hidden">
            <div className="shrink-0 px-4 pt-2.5 pb-1">
              <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                Previous emails
              </span>
            </div>
            <div className="flex-1 overflow-y-auto">
              <EmailHistory matterId={matterId} />
            </div>
          </div>

          {/* Divider */}
          <div className="shrink-0 border-t border-zinc-200 dark:border-zinc-700" />

          {!isLoading && !isKnownCaseType(draftMatterType) && draftMatterType && (
            <ManualReviewBanner matterType={draftMatterType} />
          )}

          {isLoading ? (
            <div className="flex-1 flex flex-col">
              {/* Subject skeleton */}
              <div className="shrink-0 flex items-center gap-2.5 border-b border-zinc-100 dark:border-zinc-800 px-4 py-2.5 bg-zinc-50/50 dark:bg-zinc-900/50">
                <span className="text-[11px] font-medium text-zinc-400 dark:text-zinc-500 shrink-0">
                  Subject
                </span>
                <div className="h-3.5 w-48 rounded bg-zinc-200 dark:bg-zinc-700 animate-pulse" />
              </div>
              {/* Body skeleton */}
              <div className="flex-1 px-4 py-3">
                <div className="flex items-center gap-2 mb-3">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />
                  <span className="text-xs text-zinc-500 font-medium">Generating draft...</span>
                </div>
                <div className="space-y-2">
                  <div className="h-3 w-full rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" />
                  <div className="h-3 w-full rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" />
                  <div className="h-3 w-5/6 rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" />
                  <div className="h-3 w-0 rounded" />
                  <div className="h-3 w-full rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" />
                  <div className="h-3 w-4/6 rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" />
                  <div className="h-3 w-0 rounded" />
                  <div className="h-3 w-2/3 rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" />
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Subject row */}
              <div className="shrink-0 flex items-baseline gap-2.5 border-b border-zinc-100 dark:border-zinc-800 px-4 py-2.5 bg-zinc-50/50 dark:bg-zinc-900/50">
                <span className="text-[11px] font-medium text-zinc-400 dark:text-zinc-500 shrink-0 pt-px">
                  Subject
                </span>
                <input
                  value={subject}
                  onChange={(e) => onSubjectChange(e.target.value)}
                  placeholder="Subject..."
                  className="flex-1 min-w-0 bg-transparent text-[13px] font-medium text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-300 dark:placeholder:text-zinc-600 outline-none border-0 p-0"
                />
              </div>

              {/* Body – rich text editor */}
              <div className="flex-1 overflow-y-auto px-4 py-3">
                <EmailBodyEditor
                  value={body}
                  onChange={onBodyChange}
                  placeholder="Compose email..."
                  className="min-h-full border-0 rounded-none px-0 py-0"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
